import {
  Archive,
  ArchiveRestore,
  BellPlus,
  EllipsisVertical,
  Palette,
  Pin,
  PinOff,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useReducer, useState } from 'react';
import { notifyManager, useQueryClient } from '@tanstack/react-query';
import type { NoteDto } from '@keep/shared';
import { findCachedNote } from '../../api/notes-cache';
import { IconButton } from '../../components/ui/icon-button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../components/ui/menu';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { ConfirmDialog } from '../../components/ui/dialog';
import { useUi } from '../../stores/ui';
import { ColorPicker } from './color-picker';
import { LabelPicker } from './label-picker';
import { ReminderPicker } from './reminder-picker';
import { useNoteActions } from './use-note-actions';

/** Notes sélectionnées, lues dans le cache et tenues à jour quand il change. */
function useCachedNotes(ids: ReadonlySet<string>): NoteDto[] {
  const queryClient = useQueryClient();
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  // batchCalls diffère la notification : le cache peut changer pendant le rendu d'un autre composant.
  useEffect(
    () => queryClient.getQueryCache().subscribe(notifyManager.batchCalls(rerender)),
    [queryClient],
  );
  return [...ids]
    .map((id) => findCachedNote(queryClient, id))
    .filter((note): note is NoteDto => Boolean(note));
}

/** Barre affichée à la place de l'en-tête quand des notes sont sélectionnées. */
export function SelectionBar() {
  const selection = useUi((s) => s.selection);
  const clearSelection = useUi((s) => s.clearSelection);
  const actions = useNoteActions();
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const notes = useCachedNotes(selection);
  const count = notes.length;
  const allPinned = count > 0 && notes.every((n) => n.pinned);
  const allArchived = count > 0 && notes.every((n) => n.archived);
  const trashed = count > 0 && notes.every((n) => n.trashedAt !== null);
  // Un libellé est « coché » s'il est présent sur toutes les notes sélectionnées.
  const commonLabels = notes.length
    ? notes[0]!.labelIds.filter((id) => notes.every((n) => n.labelIds.includes(id)))
    : [];

  const done = (fn: () => void) => () => {
    fn();
    clearSelection();
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center gap-1 bg-bg px-2 shadow-raised sm:px-4">
      <IconButton icon={X} label="Effacer la sélection" onClick={clearSelection} />
      <span className="flex-1 pl-2 text-[22px] text-fg">
        {count} {count > 1 ? 'notes sélectionnées' : 'note sélectionnée'}
      </span>

      {trashed ? (
        <>
          <IconButton
            icon={RotateCcw}
            label="Restaurer"
            onClick={done(() => actions.restore(notes))}
          />
          <IconButton
            icon={Trash2}
            label="Supprimer définitivement"
            onClick={() => setConfirmDelete(true)}
          />
        </>
      ) : (
        <>
          <IconButton
            icon={allPinned ? PinOff : Pin}
            label={allPinned ? 'Retirer' : 'Épingler'}
            onClick={done(() => actions.setPinned(notes, !allPinned))}
          />
          <Popover>
            <PopoverTrigger asChild>
              <IconButton icon={BellPlus} label="Me le rappeler" />
            </PopoverTrigger>
            <PopoverContent align="end">
              <ReminderPicker
                value={null}
                onChange={(value) => {
                  for (const note of notes) actions.setReminder(note, value);
                  clearSelection();
                }}
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <IconButton icon={Palette} label="Options d'arrière-plan" />
            </PopoverTrigger>
            <PopoverContent align="end" onOpenAutoFocus={(e) => e.preventDefault()}>
              <ColorPicker
                value={
                  notes.every((n) => n.color === notes[0]?.color) ? (notes[0]?.color ?? null) : null
                }
                onChange={(color) => actions.setColor(notes, color)}
              />
            </PopoverContent>
          </Popover>
          <IconButton
            icon={allArchived ? ArchiveRestore : Archive}
            label={allArchived ? 'Désarchiver' : 'Archiver'}
            onClick={done(() => (allArchived ? actions.unarchive(notes) : actions.archive(notes)))}
          />
          <Popover open={labelsOpen} onOpenChange={setLabelsOpen}>
            <PopoverAnchor asChild>
              <span className="inline-flex">
                <Menu modal={false}>
                  <MenuTrigger asChild>
                    <IconButton icon={EllipsisVertical} label="Plus" />
                  </MenuTrigger>
                  <MenuContent align="end">
                    <MenuItem onSelect={done(() => actions.trash(notes))}>Supprimer</MenuItem>
                    <MenuItem onSelect={() => setTimeout(() => setLabelsOpen(true))}>
                      Modifier les libellés
                    </MenuItem>
                    <MenuItem onSelect={done(() => actions.copy(notes))}>Créer une copie</MenuItem>
                  </MenuContent>
                </Menu>
              </span>
            </PopoverAnchor>
            <PopoverContent align="end">
              <LabelPicker
                selected={commonLabels}
                onToggle={(labelId, enabled) => actions.setLabel(notes, labelId, enabled)}
              />
            </PopoverContent>
          </Popover>
        </>
      )}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Supprimer définitivement ?"
        message={`${count > 1 ? 'Ces notes seront supprimées' : 'Cette note sera supprimée'} définitivement. Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onConfirm={done(() => actions.deleteForever(notes))}
      />
    </header>
  );
}
